import * as userApi from '@apis/admin-user';
import * as membershipApi from '@apis/memberships';
import { useAuthentication } from '@hooks/authentication';
import { AdminUser } from '@models/auth';
import { Membership } from '@models/membership';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

const DEFAULT_PAGE_SIZE = 20;

export const MembershipUsersPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState<string>();
  const [keyword, setKeyword] = useState<string | undefined>(undefined);
  const [aliasFilter, setAliasFilter] = useState<string | undefined>(undefined);
  const searchInputRef = useRef<any>();

  useAuthentication();

  const membershipsQuery = useQuery({
    queryKey: ['membership-users-memberships-dropdown'],
    queryFn: () => membershipApi.searchMemberships({ page: 0, size: 500 }),
  });

  const membershipList = (membershipsQuery.data?.data?.contents || []) as Membership[];

  const usersQuery = useQuery({
    queryKey: ['membership-users', page, size, keyword, aliasFilter],
    queryFn: () =>
      userApi.searchUsers({
        page,
        size,
        keyword,
        membershipUsers: true,
        ...(aliasFilter ? { membership: aliasFilter } : {}),
      }),
  });

  const result = usersQuery.data?.data;
  const rows = (result?.contents || []).filter((u) => u?._id);

  const getExcelFile = async () => {
    const fileName = `igogo-membership-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
    const blob = await userApi.getSearchUserssExcelFile({
      exportType: 'membershipUsers',
      keyword,
      ...(aliasFilter ? { membership: aliasFilter } : {}),
    });
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
    } else {
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode && link.parentNode.removeChild(link);
    }
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: '유저 ID',
      dataIndex: '_id',
      width: 80,
      render: (_id: string) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID
          </Button>
        </div>
      ),
    },
    {
      title: '가입한 멤버십',
      width: 180,
      render: (record) => {
        return record.membership?.name || '-';
      },
    },
    {
      title: '유저 이름',
      width: 140,
      render: (record) => record.profile?.name || record.profile?.realName || record.profile?.nickname || '-',
    },
    {
      title: '연락처',
      width: 160,
      render: (record) => record.profile?.phone || '-',
    },
    {
      title: '가입일',
      width: 180,
      render: (record) => {
        const joinDate = (record.membership as { joinDate?: string })?.joinDate;
        if (!joinDate) {
          return '-';
        }
        return moment(joinDate).format('YYYY.MM.DD HH:mm');
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>멤버십 가입 유저</div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              width: '100%',
              minHeight: '52px',
              alignItems: 'center',
            }}>
            <FilterSelect
              inactiveValue=""
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="멤버십"
              style={{ minWidth: 220 }}
              loading={membershipsQuery.isLoading}
              options={[
                { value: '', label: '전체' },
                ...membershipList.map((m) => ({ value: m.alias, label: m.name })),
              ]}
              value={aliasFilter ?? ''}
              onChange={(value) => {
                setAliasFilter(value ? String(value) : undefined);
                setPage(0);
              }}
            />
            <Input
              className={filterActiveClassName(!!keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchInput}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter') {
                  setKeyword(searchInput || undefined);
                  setPage(0);
                }
              }}
            />
            <Button
              onClick={() => {
                setSearchInput(undefined);
                setKeyword(undefined);
                setAliasFilter(undefined);
                setSize(DEFAULT_PAGE_SIZE);
                setPage(0);
              }}>
              검색 초기화
            </Button>
            <div style={{ fontWeight: 'bold' }}>검색결과: {result?.total}</div>
            <div style={{ marginLeft: 'auto' }}>
              <Button
                onClick={async () => {
                  try {
                    await getExcelFile();
                    toast.success('다운로드가 완료되었습니다', { position: 'top-center' });
                  } catch {
                    toast.error('다운로드에 실패했습니다', { position: 'top-center' });
                  }
                }}>
                검색결과 다운
              </Button>
            </div>
          </div>
          <Table
            columns={columns}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record._id}
            dataSource={rows}
            pagination={{
              current: (result?.page ?? 0) + 1,
              pageSize: result?.size || size,
              total: result?.total,
            }}
            loading={usersQuery.isLoading}
            onChange={(pag) => {
              if (pag.pageSize && pag.pageSize !== size) {
                setSize(pag.pageSize);
                setPage(0);
                return;
              }
              setPage((pag.current || 1) - 1);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MembershipUsersPage;
